/** 資金繰り(CF)のFirestoreアクセス。閲覧・更新できるのは経理と管理者のみ(セキュリティルール) */
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { dedupHash, type ImportPlanItem } from './matching';
import type { CashAccount, CashAccountKind, CashEntry, DateStr, HolidayShift, Yen } from '@core/lib/types';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

export async function fetchCashAccounts(): Promise<CashAccount[]> {
  const snap = await getDocs(query(collection(getDb(), 'cash_accounts'), orderBy('display_order')));
  return snap.docs.map((d) => withId<CashAccount>(d));
}

// --- 支払い方法(口座)マスタのCRUD(v3.7・マスタ管理→支払い方法タブ) -------

export type CashAccountInput = {
  name: string;
  kind: CashAccountKind;
  companyId: string;
  openingBalance: Yen;
  /** クレカのみ(2026-09-04): 締め日・引き落とし日・引き落とし月 */
  closingDay?: number | null;
  debitDay?: number | null;
  debitMonthOffset?: 1 | 2 | null;
  /** クレカのみ(2026-09-08): 引き落とし日が土日祝のとき */
  debitHolidayShift?: HolidayShift | null;
};

export async function createCashAccount(input: CashAccountInput, displayOrder: number): Promise<void> {
  if (!input.name.trim()) throw new Error('名称を入力してください');
  await setDoc(doc(collection(getDb(), 'cash_accounts')), {
    company_id: input.companyId,
    name: input.name.trim(),
    kind: input.kind,
    bank_name: null,
    branch_name: null,
    account_number_masked: null,
    opening_balance: input.openingBalance,
    current_balance: input.openingBalance,
    display_order: displayOrder,
    active: true,
    closing_day: input.closingDay ?? null,
    debit_day: input.debitDay ?? null,
    debit_month_offset: input.debitMonthOffset ?? null,
    debit_holiday_shift: input.debitHolidayShift ?? null,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

export async function updateCashAccount(
  id: string,
  patch: Partial<{
    name: string;
    kind: CashAccountKind;
    company_id: string;
    opening_balance: Yen;
    active: boolean;
    closing_day: number | null;
    debit_day: number | null;
    debit_month_offset: 1 | 2 | null;
    debit_holiday_shift: HolidayShift | null;
  }>,
): Promise<void> {
  await updateDoc(doc(getDb(), 'cash_accounts', id), { ...patch, updated_at: serverTimestamp() });
}

export async function fetchCashEntries(cashAccountId: string, max = 300): Promise<CashEntry[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'cash_entries'),
      where('cash_account_id', '==', cashAccountId),
      orderBy('date', 'desc'),
      limit(max),
    ),
  );
  return snap.docs.map((d) => withId<CashEntry>(d));
}

/** 支払予定(planned)を日付順で取得する。残高見込みと消込の候補に使う */
export async function fetchPlannedEntries(max = 500): Promise<CashEntry[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'cash_entries'),
      where('status', '==', 'planned'),
      orderBy('date'),
      limit(max),
    ),
  );
  return snap.docs.map((d) => withId<CashEntry>(d));
}

/** 新しい入出金のIDを先に作る(バッチ書き込みで取引と相互参照するため) */
export function newCashEntryId(): string {
  return doc(collection(getDb(), 'cash_entries')).id;
}

export type ManualEntryInput = {
  cashAccountId: string;
  date: DateStr;
  direction: 'in' | 'out';
  amount: Yen;
  description: string;
  counterparty: string;
  status: 'planned' | 'settled';
};

/** 手入力で1件登録する */
export async function createManualEntry(input: ManualEntryInput, userId: string): Promise<void> {
  const id = newCashEntryId();
  const batch = writeBatch(getDb());
  batch.set(doc(getDb(), 'cash_entries', id), {
    cash_account_id: input.cashAccountId,
    date: input.date,
    direction: input.direction,
    amount: input.amount,
    status: input.status,
    description: input.description || null,
    counterparty: input.counterparty || null,
    transaction_id: null,
    import_batch_id: null,
    dedup_hash:
      input.status === 'settled'
        ? dedupHash({
            cashAccountId: input.cashAccountId,
            date: input.date,
            direction: input.direction,
            amount: input.amount,
            description: input.description,
          })
        : null,
    matched_at: null,
    matched_by: null,
    created_by: userId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * 取込計画を実行する。
 * - 消込(chosenEntryId あり): 既存の planned 行を settled に更新し、実際の日付・摘要で上書きする
 *   → **新しい行は作らない**(二重計上の防止)
 * - 新規(chosenEntryId なし): settled の行を新規作成する
 * - 重複(duplicate): 何もしない
 */
export async function commitImportPlan(params: {
  cashAccountId: string;
  fileName: string;
  items: ImportPlanItem[];
  errorRows: number;
  userId: string;
}): Promise<{ matched: number; inserted: number; duplicates: number }> {
  const { cashAccountId, fileName, items, errorRows, userId } = params;
  const batchId = doc(collection(getDb(), 'cash_imports')).id;
  const db = getDb();

  let matched = 0;
  let inserted = 0;
  const duplicates = items.filter((i) => i.kind === 'duplicate').length;

  // Firestoreのバッチ上限(500)に収まるよう分割する
  const targets = items.filter((i) => i.kind !== 'duplicate');
  const chunkSize = 200;

  for (let offset = 0; offset < targets.length; offset += chunkSize) {
    const batch = writeBatch(db);
    for (const item of targets.slice(offset, offset + chunkSize)) {
      if (item.chosenEntryId) {
        batch.update(doc(db, 'cash_entries', item.chosenEntryId), {
          status: 'settled',
          date: item.row.date,
          amount: item.row.amount,
          description: item.row.description || null,
          dedup_hash: item.hash,
          import_batch_id: batchId,
          matched_at: serverTimestamp(),
          matched_by: userId,
          updated_at: serverTimestamp(),
        });
        matched++;
      } else {
        batch.set(doc(db, 'cash_entries', newCashEntryId()), {
          cash_account_id: cashAccountId,
          date: item.row.date,
          direction: item.row.direction,
          amount: item.row.amount,
          status: 'settled',
          description: item.row.description || null,
          counterparty: null,
          transaction_id: null,
          import_batch_id: batchId,
          dedup_hash: item.hash,
          matched_at: null,
          matched_by: null,
          created_by: userId,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
        inserted++;
      }
    }
    await batch.commit();
  }

  await writeBatch(db)
    .set(doc(db, 'cash_imports', batchId), {
      cash_account_id: cashAccountId,
      file_name: fileName,
      format: 'generic-csv',
      imported_at: serverTimestamp(),
      imported_by: userId,
      row_count: items.length + errorRows,
      inserted_count: inserted,
      matched_count: matched,
      duplicate_count: duplicates,
      error_rows: errorRows,
    })
    .commit();

  return { matched, inserted, duplicates };
}

export async function fetchImportHistory(cashAccountId: string, max = 20) {
  const snap = await getDocs(
    query(
      collection(getDb(), 'cash_imports'),
      where('cash_account_id', '==', cashAccountId),
      orderBy('imported_at', 'desc'),
      limit(max),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) }));
}

/**
 * 口座残高を cash_entries から算出する。
 * 期首残高 + 確定(settled)の入出金。予定(planned)は含めない。
 * ※ cash_accounts.current_balance は将来の集計キャッシュ用で、画面では使わない(ずれを持ち込まないため)
 */
export function calcBalance(account: CashAccount, entries: CashEntry[]): Yen {
  return entries
    .filter((e) => e.status === 'settled')
    .reduce((sum, e) => sum + (e.direction === 'in' ? e.amount : -e.amount), account.opening_balance);
}

/** 指定日までの支払予定を反映した残高見込み */
export function calcForecast(balance: Yen, entries: CashEntry[], until: DateStr): Yen {
  return entries
    .filter((e) => e.status === 'planned' && e.date <= until)
    .reduce((sum, e) => sum + (e.direction === 'in' ? e.amount : -e.amount), balance);
}
